import {
  SalaryCalculationInput,
  SalaryCalculationResult,
  NetToGrossSolverResult,
  ProgressiveBracketBreakdownItem,
  CalculationTraceStep,
  TaxBracket,
  PayFrequency,
  GoldenTaxTestCase,
} from '../types';
import { SALARY_COUNTRY_PACKS, GOLDEN_TEST_CASES } from '../data/salaryTaxPacksData';

export class SalaryCalculationEngine {
  private static instance: SalaryCalculationEngine;

  private constructor() {}

  public static getInstance(): SalaryCalculationEngine {
    if (!SalaryCalculationEngine.instance) {
      SalaryCalculationEngine.instance = new SalaryCalculationEngine();
    }
    return SalaryCalculationEngine.instance;
  }

  /**
   * Convert any pay frequency amount into Annual Gross
   */
  public toAnnualGross(
    amount: number,
    frequency: PayFrequency,
    hoursPerWeek: number = 40,
    weeksPerYear: number = 52
  ): number {
    if (amount <= 0) return 0;
    switch (frequency) {
      case 'hourly':
        return amount * hoursPerWeek * weeksPerYear;
      case 'daily':
        return amount * 5 * weeksPerYear;
      case 'weekly':
        return amount * weeksPerYear;
      case 'biweekly':
        return amount * 26;
      case 'semimonthly':
        return amount * 24;
      case 'monthly':
        return amount * 12;
      case 'quarterly':
        return amount * 4;
      case 'annual':
      default:
        return amount;
    }
  }

  /**
   * Convert Annual amount to specified pay period
   */
  public fromAnnual(
    annualAmount: number,
    frequency: PayFrequency,
    hoursPerWeek: number = 40,
    weeksPerYear: number = 52
  ): number {
    switch (frequency) {
      case 'hourly':
        return hoursPerWeek * weeksPerYear > 0 ? annualAmount / (hoursPerWeek * weeksPerYear) : 0;
      case 'daily':
        return weeksPerYear * 5 > 0 ? annualAmount / (weeksPerYear * 5) : 0;
      case 'weekly':
        return annualAmount / weeksPerYear;
      case 'biweekly':
        return annualAmount / 26;
      case 'semimonthly':
        return annualAmount / 24;
      case 'monthly':
        return annualAmount / 12;
      case 'quarterly':
        return annualAmount / 4;
      case 'annual':
      default:
        return annualAmount;
    }
  }

  /**
   * Progressive Bracket Decomposition Calculator (Pure deterministic math)
   */
  public computeProgressiveBrackets(
    taxableIncome: number,
    brackets: TaxBracket[]
  ): { totalTax: number; breakdowns: ProgressiveBracketBreakdownItem[]; topMarginalRate: number } {
    let totalTax = 0;
    let topMarginalRate = 0;
    const breakdowns: ProgressiveBracketBreakdownItem[] = [];

    if (taxableIncome <= 0 || !brackets || brackets.length === 0) {
      return { totalTax: 0, breakdowns: [], topMarginalRate: 0 };
    }

    for (let i = 0; i < brackets.length; i++) {
      const b = brackets[i];
      const min = b.min;
      const max = b.max;
      const rate = b.rate;

      if (taxableIncome > min) {
        const upperLimit = max !== null ? Math.min(taxableIncome, max) : taxableIncome;
        const taxableInThisBracket = Math.max(0, upperLimit - min);
        const taxInThisBracket = taxableInThisBracket * rate;

        if (taxableInThisBracket > 0 && rate > topMarginalRate) {
          topMarginalRate = rate;
        }

        totalTax += taxInThisBracket;
        breakdowns.push({
          bracketName: b.label || `${(rate * 100).toFixed(1)}% Tier`,
          min,
          max,
          taxableInBracket: Math.round(taxableInThisBracket * 100) / 100,
          rate,
          taxAmount: Math.round(taxInThisBracket * 100) / 100,
        });
      } else {
        // Did not reach this tier
        breakdowns.push({
          bracketName: b.label || `${(rate * 100).toFixed(1)}% Tier`,
          min,
          max,
          taxableInBracket: 0,
          rate,
          taxAmount: 0,
        });
      }
    }

    return {
      totalTax: Math.round(totalTax * 100) / 100,
      breakdowns,
      topMarginalRate,
    };
  }

  /**
   * PRIMARY PIPELINE: Full Gross-to-Net & Paycheck Calculation
   */
  public calculateSalary(input: SalaryCalculationInput): SalaryCalculationResult {
    const calculationId = `CALC-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const calculatedAt = new Date().toISOString();

    const countryPack = SALARY_COUNTRY_PACKS[input.countryCode] || SALARY_COUNTRY_PACKS['US'];
    const currency = countryPack.defaultCurrency;
    const hoursPerWeek = input.hoursPerWeek || countryPack.hoursPerWeekDefault || 40;
    const weeksPerYear = input.weeksPerYear || countryPack.weeksPerYearDefault || 52;
    const filingStatus = input.filingStatus || 'SINGLE';

    const trace: CalculationTraceStep[] = [];
    let stepCount = 1;

    // 1. Gross Normalization
    const annualGross = this.toAnnualGross(input.grossAmount, input.payFrequency, hoursPerWeek, weeksPerYear);
    const periodGross = this.fromAnnual(annualGross, input.payFrequency, hoursPerWeek, weeksPerYear);
    const monthlyGross = annualGross / 12;

    trace.push({
      stepNumber: stepCount++,
      name: 'Gross Compensation Normalization',
      category: 'GROSS',
      description: `Normalized input salary of ${input.grossAmount.toLocaleString()} (${input.payFrequency}) to annualized gross.`,
      amountBefore: input.grossAmount,
      adjustment: annualGross - input.grossAmount,
      amountAfter: annualGross,
      ruleReference: 'Base Compensation Normalization',
      formula: `Annual Gross = ${annualGross.toLocaleString()} ${currency}`,
    });

    // 2. Pre-Tax Deductions (401k, pension, health)
    let preTaxDeductionsTotal = 0;
    if (input.preTaxDeductions && input.preTaxDeductions.length > 0) {
      input.preTaxDeductions.forEach((d) => {
        const val = d.isPercentage ? annualGross * (d.amount / 100) : d.amount;
        preTaxDeductionsTotal += val;
      });
    }

    // Add optional custom employee pension percentage if provided
    if (input.pensionEmployeePercent && input.pensionEmployeePercent > 0) {
      const pensionAmount = annualGross * (input.pensionEmployeePercent / 100);
      preTaxDeductionsTotal += pensionAmount;
    }

    if (preTaxDeductionsTotal > 0) {
      trace.push({
        stepNumber: stepCount++,
        name: 'Pre-Tax Adjustments',
        category: 'PRE_TAX',
        description: 'Deducted eligible pre-tax benefits and voluntary retirement salary sacrifice.',
        amountBefore: annualGross,
        adjustment: -preTaxDeductionsTotal,
        amountAfter: annualGross - preTaxDeductionsTotal,
        ruleReference: 'Section 401(k) / Salary Sacrifice Provisions',
        formula: `Adjusted Gross = ${annualGross.toLocaleString()} - ${preTaxDeductionsTotal.toLocaleString()}`,
      });
    }

    const adjustedGross = Math.max(0, annualGross - preTaxDeductionsTotal);

    // 3. Standard Deduction / Personal Allowance Resolution (with phase-out)
    let standardDeduction = countryPack.standardDeductions[filingStatus] || countryPack.standardDeductions['STANDARD'] || 0;

    // UK Personal Allowance Taper (£1 for every £2 over £100,000)
    if (input.countryCode === 'GB' && adjustedGross > 100000) {
      const excess = adjustedGross - 100000;
      const reduction = Math.min(standardDeduction, excess / 2);
      standardDeduction = Math.max(0, standardDeduction - reduction);
    }

    // France 10% standard professional expenses deduction
    if (input.countryCode === 'FR') {
      const professionalExpenseDeduction = Math.min(14171, Math.max(495, adjustedGross * 0.10));
      standardDeduction = professionalExpenseDeduction;
    }

    const taxableIncome = Math.max(0, adjustedGross - standardDeduction);

    trace.push({
      stepNumber: stepCount++,
      name: 'Taxable Income Base Resolution',
      category: 'TAXABLE',
      description: `Applied statutory standard deduction / personal allowance of ${standardDeduction.toLocaleString()} ${currency} for ${filingStatus} filing status.`,
      amountBefore: adjustedGross,
      adjustment: -standardDeduction,
      amountAfter: taxableIncome,
      ruleReference: `${countryPack.countryName} Statutory Allowance Schedule`,
      formula: `Taxable Base = max(0, Adjusted Gross - Standard Deduction)`,
    });

    // 4. Federal / National Progressive Income Tax
    const federalBracketsList =
      countryPack.federalBrackets[filingStatus] || countryPack.federalBrackets['STANDARD'] || [];
    const federalResult = this.computeProgressiveBrackets(taxableIncome, federalBracketsList);
    let federalIncomeTax = federalResult.totalTax;
    let marginalTaxRate = federalResult.topMarginalRate;

    trace.push({
      stepNumber: stepCount++,
      name: `${countryPack.countryName} Federal/National Income Tax`,
      category: 'PROGRESSIVE_TAX',
      description: `Progressively taxed across ${federalResult.breakdowns.length} statutory brackets (Top marginal: ${(marginalTaxRate * 100).toFixed(1)}%).`,
      amountBefore: taxableIncome,
      adjustment: -federalIncomeTax,
      amountAfter: taxableIncome - federalIncomeTax,
      ruleReference: countryPack.rulesetVersion,
      formula: `Federal Tax = Σ(Taxable in Bracket_i × Rate_i)`,
    });

    // 5. State / Provincial / Regional Taxes
    let stateIncomeTax = 0;
    let localIncomeTax = 0;
    let stateBracketsBreakdown: ProgressiveBracketBreakdownItem[] = [];

    const selectedSubdivision = countryPack.subdivisions.find((s) => s.code === input.subdivisionCode);

    if (selectedSubdivision && selectedSubdivision.hasStateTax) {
      if (selectedSubdivision.brackets && selectedSubdivision.brackets.length > 0) {
        const stateStdDeduction =
          selectedSubdivision.standardDeductions?.[filingStatus] || selectedSubdivision.standardDeductions?.['SINGLE'] || 0;
        const stateTaxable = Math.max(0, adjustedGross - stateStdDeduction);
        const stateRes = this.computeProgressiveBrackets(stateTaxable, selectedSubdivision.brackets);
        stateIncomeTax = stateRes.totalTax;
        stateBracketsBreakdown = stateRes.breakdowns;
        if (stateRes.topMarginalRate > 0) {
          marginalTaxRate += stateRes.topMarginalRate;
        }
      } else if (selectedSubdivision.flatRate && selectedSubdivision.flatRate > 0) {
        const stateStdDeduction =
          selectedSubdivision.standardDeductions?.[filingStatus] || selectedSubdivision.standardDeductions?.['SINGLE'] || 0;
        const stateTaxable = Math.max(0, adjustedGross - stateStdDeduction);
        stateIncomeTax = stateTaxable * selectedSubdivision.flatRate;
        stateBracketsBreakdown = [
          {
            bracketName: `${(selectedSubdivision.flatRate * 100).toFixed(2)}% Flat Rate`,
            min: 0,
            max: null,
            taxableInBracket: stateTaxable,
            rate: selectedSubdivision.flatRate,
            taxAmount: stateIncomeTax,
          },
        ];
        marginalTaxRate += selectedSubdivision.flatRate;
      }

      // Check Local / Municipality surcharges (e.g. NYC, Yonkers)
      if (input.localJurisdictionCode && selectedSubdivision.localJurisdictions) {
        const local = selectedSubdivision.localJurisdictions.find((l) => l.code === input.localJurisdictionCode);
        if (local) {
          localIncomeTax = taxableIncome * local.rate;
        }
      }

      trace.push({
        stepNumber: stepCount++,
        name: `${selectedSubdivision.name} State/Provincial Tax`,
        category: 'PROGRESSIVE_TAX',
        description: `Applied ${selectedSubdivision.name} tax schedules and local surcharges.`,
        amountBefore: taxableIncome,
        adjustment: -stateIncomeTax - localIncomeTax,
        amountAfter: taxableIncome - stateIncomeTax - localIncomeTax,
        ruleReference: selectedSubdivision.officialSource,
        formula: `State Tax = ${stateIncomeTax.toLocaleString()} | Local = ${localIncomeTax.toLocaleString()} ${currency}`,
      });
    }

    const totalIncomeTax = federalIncomeTax + stateIncomeTax + localIncomeTax;

    // 6. Employee Social Contributions (FICA / CPP / EI / NI / URSSAF / CPF / EPF)
    let employeeSocialSecurity = 0;
    let employeeMedicare = 0;
    let employeePension = 0;
    let employeeOtherContributions = 0;

    countryPack.socialContributions.forEach((contrib) => {
      let subjectWage = annualGross;
      if (contrib.wageCap !== null) {
        subjectWage = Math.min(subjectWage, contrib.wageCap);
      }
      subjectWage = Math.max(0, subjectWage - contrib.exemptionThreshold);

      let amount = subjectWage * contrib.employeeRate;

      // Check additional threshold (e.g. US Additional Medicare >$200k)
      if (contrib.additionalRate && contrib.additionalThreshold && annualGross > contrib.additionalThreshold) {
        const excess = annualGross - contrib.additionalThreshold;
        amount += excess * contrib.additionalRate;
      }

      if (contrib.id.includes('ss') || contrib.id.includes('fica_ss')) {
        employeeSocialSecurity += amount;
      } else if (contrib.id.includes('med') || contrib.id.includes('fica_med') || contrib.id.includes('kv')) {
        employeeMedicare += amount;
      } else if (contrib.id.includes('pension') || contrib.id.includes('rv') || contrib.id.includes('cpp')) {
        employeePension += amount;
      } else {
        employeeOtherContributions += amount;
      }
    });

    const totalEmployeeContributions =
      employeeSocialSecurity + employeeMedicare + employeePension + employeeOtherContributions;

    trace.push({
      stepNumber: stepCount++,
      name: 'Mandatory Employee Social & Pension Contributions',
      category: 'SOCIAL_CONTRIBUTIONS',
      description: 'Calculated statutory employee contributions (Social Security, Medicare/Health, Pension, Unemployment).',
      amountBefore: annualGross,
      adjustment: -totalEmployeeContributions,
      amountAfter: annualGross - totalIncomeTax - totalEmployeeContributions,
      ruleReference: 'Statutory Social Security Wage Base Schedules',
      formula: `Social Contributions = ${totalEmployeeContributions.toLocaleString()} ${currency}`,
    });

    // 7. Post-Tax Deductions (Garnishments, union dues, voluntary benefits)
    let postTaxDeductionsTotal = 0;
    if (input.postTaxDeductions && input.postTaxDeductions.length > 0) {
      input.postTaxDeductions.forEach((d) => {
        const val = d.isPercentage ? annualGross * (d.amount / 100) : d.amount;
        postTaxDeductionsTotal += val;
      });
    }

    const taxCreditsTotal = 0; // Baseline standard credits included in brackets/allowances

    // 8. Net Pay & Take-Home
    const annualNetPay = Math.max(0, annualGross - totalIncomeTax - totalEmployeeContributions - postTaxDeductionsTotal);
    const monthlyNetPay = annualNetPay / 12;
    const periodNetPay = this.fromAnnual(annualNetPay, input.payFrequency, hoursPerWeek, weeksPerYear);
    const hourlyEquivalentNet = hoursPerWeek * weeksPerYear > 0 ? annualNetPay / (hoursPerWeek * weeksPerYear) : 0;

    // Rates
    const effectiveTaxRate = annualGross > 0 ? totalIncomeTax / annualGross : 0;
    const totalDeductionRate = annualGross > 0 ? (totalIncomeTax + totalEmployeeContributions + postTaxDeductionsTotal) / annualGross : 0;

    trace.push({
      stepNumber: stepCount++,
      name: 'Net Take-Home Pay Resolution',
      category: 'NET',
      description: `Final net take-home pay resolved after all statutory taxes, social security, and payroll deductions.`,
      amountBefore: annualGross,
      adjustment: -(annualGross - annualNetPay),
      amountAfter: annualNetPay,
      ruleReference: 'Net = Gross - Income Taxes - Employee Contributions - Post-Tax Deductions',
      formula: `Net Take-Home = ${annualNetPay.toLocaleString()} ${currency} / year (${monthlyNetPay.toLocaleString(undefined, { maximumFractionDigits: 2 })} / month)`,
    });

    // 9. Employer Cost Engine (Total Cost of Employment)
    let employerSocialSecurity = 0;
    let employerMedicare = 0;
    let employerPension = 0;
    let employerOtherContributions = 0;

    countryPack.employerContributions.forEach((contrib) => {
      let subjectWage = annualGross;
      if (contrib.wageCap !== null) {
        subjectWage = Math.min(subjectWage, contrib.wageCap);
      }
      subjectWage = Math.max(0, subjectWage - contrib.exemptionThreshold);
      const amount = subjectWage * contrib.employerRate;

      if (contrib.id.includes('ss') || contrib.id.includes('fica_ss')) {
        employerSocialSecurity += amount;
      } else if (contrib.id.includes('med') || contrib.id.includes('fica_med')) {
        employerMedicare += amount;
      } else if (contrib.id.includes('pension') || contrib.id.includes('cpp') || contrib.id.includes('superannuation')) {
        employerPension += amount;
      } else {
        employerOtherContributions += amount;
      }
    });

    // Add optional custom employer pension matching if provided
    if (input.pensionEmployerPercent && input.pensionEmployerPercent > 0) {
      const matchAmount = annualGross * (input.pensionEmployerPercent / 100);
      employerPension += matchAmount;
    }

    const totalEmployerContributions =
      employerSocialSecurity + employerMedicare + employerPension + employerOtherContributions;
    const totalEmployerCost = annualGross + totalEmployerContributions;
    const employerCostRatio = annualGross > 0 ? totalEmployerCost / annualGross : 1;

    trace.push({
      stepNumber: stepCount++,
      name: 'Total Employer Cost of Employment',
      category: 'EMPLOYER',
      description: 'Calculated statutory employer-side taxes, matching contributions, and mandatory social levies.',
      amountBefore: annualGross,
      adjustment: totalEmployerContributions,
      amountAfter: totalEmployerCost,
      ruleReference: 'Employer Statutory Contribution Schedule',
      formula: `Employer Cost = ${annualGross.toLocaleString()} + ${totalEmployerContributions.toLocaleString()} = ${totalEmployerCost.toLocaleString()} ${currency} (${(employerCostRatio * 100).toFixed(1)}% of gross)`,
    });

    // 10. Payroll Withholding vs Annual Liability Note
    const withholdingAnnualEstimate = totalIncomeTax * 1.02; // Typical payroll tables include minor safe-harbor withholding buffer
    const annualLiabilityEstimate = totalIncomeTax;
    const withholdingNote =
      'Payroll Withholding vs. Annual Liability: Payroll systems withhold estimated tax per pay period. Your actual year-end tax liability may include additional personal tax credits, deductions, or investment income upon filing your annual tax return.';

    const plainLanguageExplanation = `For an annual gross compensation of ${annualGross.toLocaleString()} ${currency} in ${countryPack.countryName}${
      selectedSubdivision ? ` (${selectedSubdivision.name})` : ''
    }, your estimated net take-home pay is ${annualNetPay.toLocaleString(undefined, {
      maximumFractionDigits: 0,
    })} ${currency} per year (${monthlyNetPay.toLocaleString(undefined, {
      maximumFractionDigits: 0,
    })} ${currency}/month). Total income taxes are ${(effectiveTaxRate * 100).toFixed(1)}% (${totalIncomeTax.toLocaleString()} ${currency}) with a top marginal rate of ${(
      marginalTaxRate * 100
    ).toFixed(1)}%, and employee social contributions total ${totalEmployeeContributions.toLocaleString()} ${currency}. Your employer's estimated total employment cost is ${totalEmployerCost.toLocaleString(
      undefined,
      { maximumFractionDigits: 0 }
    )} ${currency}.`;

    return {
      calculationId,
      calculatedAt,
      input,
      countryName: countryPack.countryName,
      jurisdictionName: selectedSubdivision ? `${selectedSubdivision.name}, ${countryPack.countryName}` : countryPack.countryName,
      currency,
      annualGross: Math.round(annualGross * 100) / 100,
      periodGross: Math.round(periodGross * 100) / 100,
      monthlyGross: Math.round(monthlyGross * 100) / 100,
      preTaxDeductionsTotal: Math.round(preTaxDeductionsTotal * 100) / 100,
      standardDeductionApplied: Math.round(standardDeduction * 100) / 100,
      taxableIncome: Math.round(taxableIncome * 100) / 100,
      federalIncomeTax: Math.round(federalIncomeTax * 100) / 100,
      stateIncomeTax: Math.round(stateIncomeTax * 100) / 100,
      localIncomeTax: Math.round(localIncomeTax * 100) / 100,
      totalIncomeTax: Math.round(totalIncomeTax * 100) / 100,
      employeeSocialSecurity: Math.round(employeeSocialSecurity * 100) / 100,
      employeeMedicare: Math.round(employeeMedicare * 100) / 100,
      employeePension: Math.round(employeePension * 100) / 100,
      employeeOtherContributions: Math.round(employeeOtherContributions * 100) / 100,
      totalEmployeeContributions: Math.round(totalEmployeeContributions * 100) / 100,
      taxCreditsTotal,
      postTaxDeductionsTotal: Math.round(postTaxDeductionsTotal * 100) / 100,
      annualNetPay: Math.round(annualNetPay * 100) / 100,
      monthlyNetPay: Math.round(monthlyNetPay * 100) / 100,
      periodNetPay: Math.round(periodNetPay * 100) / 100,
      hourlyEquivalentNet: Math.round(hourlyEquivalentNet * 100) / 100,
      effectiveTaxRate: Math.round(effectiveTaxRate * 10000) / 10000,
      marginalTaxRate: Math.round(marginalTaxRate * 10000) / 10000,
      totalDeductionRate: Math.round(totalDeductionRate * 10000) / 10000,
      withholdingAnnualEstimate: Math.round(withholdingAnnualEstimate * 100) / 100,
      annualLiabilityEstimate: Math.round(annualLiabilityEstimate * 100) / 100,
      withholdingNote,
      employerSocialSecurity: Math.round(employerSocialSecurity * 100) / 100,
      employerMedicare: Math.round(employerMedicare * 100) / 100,
      employerPension: Math.round(employerPension * 100) / 100,
      employerOtherContributions: Math.round(employerOtherContributions * 100) / 100,
      totalEmployerContributions: Math.round(totalEmployerContributions * 100) / 100,
      totalEmployerCost: Math.round(totalEmployerCost * 100) / 100,
      employerCostRatio: Math.round(employerCostRatio * 10000) / 10000,
      federalBracketsBreakdown: federalResult.breakdowns,
      stateBracketsBreakdown,
      explainabilityTrace: trace,
      plainLanguageExplanation,
      rulesetVersion: countryPack.rulesetVersion,
      engineVersion: 'yamoo-payroll-engine-6.0',
      confidence: 'HIGH',
      officialSources: countryPack.officialSources,
      disclaimer:
        'YamooPay provides financial estimates based on published tax and payroll rules for informational purposes. Your actual payslip or tax liability may vary based on individual tax situations, credits, and employer payroll configurations. This does not constitute legal or certified tax advice.',
    };
  }

  /**
   * REVERSE SOLVER: Net-to-Gross Numerical Binary Search Solver
   * Solves: Find Gross such that calculateSalary(Gross).netPay == targetNet
   */
  public solveNetToGross(
    targetNet: number,
    targetPeriod: PayFrequency,
    baseInput: Omit<SalaryCalculationInput, 'grossAmount' | 'payFrequency' | 'mode'>
  ): NetToGrossSolverResult {
    if (targetNet <= 0) {
      const zeroCalc = this.calculateSalary({
        ...baseInput,
        mode: 'net-to-gross',
        grossAmount: 0,
        payFrequency: targetPeriod,
      });
      return {
        targetNet: 0,
        targetPeriod,
        requiredGrossAnnual: 0,
        requiredGrossPeriod: 0,
        requiredGrossMonthly: 0,
        iterationsCount: 0,
        converged: true,
        toleranceAchieved: 0,
        forwardCalculation: zeroCalc,
      };
    }

    const countryPack = SALARY_COUNTRY_PACKS[baseInput.countryCode] || SALARY_COUNTRY_PACKS['US'];
    const hoursPerWeek = baseInput.hoursPerWeek || countryPack.hoursPerWeekDefault || 40;
    const weeksPerYear = baseInput.weeksPerYear || countryPack.weeksPerYearDefault || 52;

    const targetNetAnnual = this.toAnnualGross(targetNet, targetPeriod, hoursPerWeek, weeksPerYear);

    // Monotonic bounds
    let lowGross = targetNetAnnual;
    let highGross = targetNetAnnual * 3.5; // Top tax rate rarely exceeds 65-70% total deductions
    let iterations = 0;
    const maxIterations = 35;
    const tolerance = 0.05; // 5 cents tolerance

    let bestGross = (lowGross + highGross) / 2;
    let bestResult = this.calculateSalary({
      ...baseInput,
      mode: 'net-to-gross',
      grossAmount: bestGross,
      payFrequency: 'annual',
    });

    while (iterations < maxIterations) {
      iterations++;
      const midGross = (lowGross + highGross) / 2;

      const calc = this.calculateSalary({
        ...baseInput,
        mode: 'net-to-gross',
        grossAmount: midGross,
        payFrequency: 'annual',
      });

      const currentNetAnnual = calc.annualNetPay;
      const diff = currentNetAnnual - targetNetAnnual;

      bestGross = midGross;
      bestResult = calc;

      if (Math.abs(diff) <= tolerance) {
        break;
      }

      if (diff < 0) {
        // Net is too low -> need higher gross
        lowGross = midGross;
      } else {
        // Net is too high -> need lower gross
        highGross = midGross;
      }
    }

    const requiredGrossAnnual = Math.round(bestGross * 100) / 100;
    const requiredGrossPeriod = Math.round(this.fromAnnual(requiredGrossAnnual, targetPeriod, hoursPerWeek, weeksPerYear) * 100) / 100;
    const requiredGrossMonthly = Math.round((requiredGrossAnnual / 12) * 100) / 100;
    const toleranceAchieved = Math.abs(bestResult.annualNetPay - targetNetAnnual);

    return {
      targetNet,
      targetPeriod,
      requiredGrossAnnual,
      requiredGrossPeriod,
      requiredGrossMonthly,
      iterationsCount: iterations,
      converged: toleranceAchieved <= 1.0,
      toleranceAchieved: Math.round(toleranceAchieved * 100) / 100,
      forwardCalculation: bestResult,
    };
  }

  /**
   * Run and verify Golden Tax Test Cases
   */
  public runGoldenTests(): {
    totalTests: number;
    passedCount: number;
    failedCount: number;
    results: (GoldenTaxTestCase & { actualNet: number; actualTax: number; deltaNet: number })[];
  } {
    const results = GOLDEN_TEST_CASES.map((tc) => {
      const calc = this.calculateSalary({
        mode: 'salary-calculator',
        countryCode: tc.countryCode,
        subdivisionCode: tc.subdivisionCode,
        grossAmount: tc.grossAnnual,
        payFrequency: 'annual',
        taxPeriodId: tc.taxYear,
        filingStatus: tc.filingStatus,
        estimationLevel: 'DETAILED_ESTIMATE',
      });

      const deltaNet = Math.abs(calc.annualNetPay - tc.expectedNetAnnual);
      const passed = deltaNet <= tc.tolerance;

      return {
        ...tc,
        actualNet: calc.annualNetPay,
        actualTax: calc.totalIncomeTax,
        deltaNet,
        status: passed ? ('PASS' as const) : ('FAIL' as const),
      };
    });

    const passedCount = results.filter((r) => r.status === 'PASS').length;

    return {
      totalTests: results.length,
      passedCount,
      failedCount: results.length - passedCount,
      results,
    };
  }
}

export const salaryEngine = SalaryCalculationEngine.getInstance();
